import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { addDays, startOfDay } from "date-fns";

export const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  selectsRange = true,
  maxRangeDays,
  ...props
}) => {
  const [selectedDate, setSelectedDate] = useState(startDate);
  const [dateRange, setDateRange] = useState(
    selectsRange ? [startDate, endDate] : [startDate, null]
  );
  const [start, end] = dateRange;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Update internal state when props change
    if (selectsRange) {
      setDateRange([startDate, endDate]);
    } else {
      setSelectedDate(startDate);
    }
  }, [startDate, endDate, selectsRange]);

  const handleChange = (update) => {
    if (selectsRange) {
      setDateRange(update);

      // Always call onChange with the update
      onChange?.(update);
    } else {
      setSelectedDate(update);
      onChange?.([update, null]);
    }
  };

  const classes = classNames(
    "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm",
    props.className
  );

  const isSelectingRangeEnd =
    selectsRange && maxRangeDays && start && !end;

  const rangeMaxDate = isSelectingRangeEnd
    ? startOfDay(addDays(start, maxRangeDays - 1))
    : props.maxDate;

  if (!selectsRange) {
    return (
      <div className="relative z-50">
        <DatePicker
          selected={selectedDate}
          onChange={handleChange}
          monthsShown={1}
          className={classes}
          customInput={props.customInput || <CustomInput />}
          popperClassName="react-datepicker-popper"
          popperPlacement="bottom-start"
          dateFormat="MMM dd, yyyy"
          {...props}
        />
      </div>
    );
  }

  return (
    <div className="relative z-50">
      <DatePicker
        selectsRange={true}
        startDate={start}
        endDate={end}
        onChange={handleChange}
        monthsShown={isMobile ? 1 : 2}
        className={classes}
        customInput={props.customInput || <CustomInput />}
        popperClassName="react-datepicker-popper"
        popperPlacement="bottom-start"
        maxDate={rangeMaxDate}
        {...props}
      />
    </div>
  );
};

const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
  <div
    className="flex items-center justify-between cursor-pointer bg-white border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2D9682] focus:border-[#2D9682]"
    onClick={onClick}
    ref={ref}
  >
    <span className="text-gray-700">{value || "Select date"}</span>
    <CalendarIcon className="w-5 h-5 text-[#2D9682]" />
  </div>
));

export default DateRangePicker;
